import {
  DuplicateRecipientCouponEntry,
  GiftCoupon,
  GiftCouponForStore,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  DuplicateRecipientDeliveryEntry,
  GiftDeliveryForStore,
} from '@shared/api/giftDelivery/giftDelivery.interface';

/**
 * 카카오톡 다건수신(같은 사람에게 여러 답례품 전달) 판정 공통 모듈.
 *
 * 백엔드는 카톡 공유 웹훅 수신 시 다음 두 필드를 자동 갱신한다.
 *   - `lastSharedKakaoHashChatId`: 가장 마지막 공유의 hashChatId
 *   - `duplicateRecipientCoupons|Deliveries`: 같은 주문 내 다른 답례품들 중
 *      동일 hashChatId 로 전달된 기록 (없으면 빈 배열)
 *
 * 프론트는 단순히 배열 길이만 보지 않고 반드시
 *   `self.lastSharedKakaoHashChatId === entry.hashChatId`
 * 인 항목으로 한 번 더 필터링해야 한다. 그래야 사용자가 다른 카톡방으로
 * 재전달했을 때 안내가 자동으로 사라진다 (재전달 → 새 webhook → 새 hashChatId).
 */

export interface DuplicateRecipientConflict {
  /** 충돌 답례품의 순번(#N번째) */
  seq: number;
  /** 충돌 답례품의 code */
  code: string;
}

const filterByLastHash = <T extends { hashChatId: string; seq: number; code: string }>(
  lastHash: string | undefined,
  entries: T[] | undefined,
): DuplicateRecipientConflict[] => {
  if (!lastHash) return [];
  if (!entries || entries.length === 0) return [];
  return entries
    .filter((e) => e.hashChatId === lastHash)
    .map((e) => ({ seq: e.seq, code: e.code }));
};

export const getDuplicateRecipientConflictsForCoupon = (
  coupon: Pick<
    GiftCoupon | GiftCouponForStore,
    'lastSharedKakaoHashChatId' | 'duplicateRecipientCoupons'
  >,
): DuplicateRecipientConflict[] =>
  filterByLastHash<DuplicateRecipientCouponEntry>(
    coupon.lastSharedKakaoHashChatId,
    coupon.duplicateRecipientCoupons,
  );

export const getDuplicateRecipientConflictsForDelivery = (
  delivery: Pick<
    GiftDeliveryForStore,
    'lastSharedKakaoHashChatId' | 'duplicateRecipientDeliveries'
  >,
): DuplicateRecipientConflict[] =>
  filterByLastHash<DuplicateRecipientDeliveryEntry>(
    delivery.lastSharedKakaoHashChatId,
    delivery.duplicateRecipientDeliveries,
  );

/**
 * 충돌 항목들을 "#1, #3" 같은 라벨 문자열로 합친다.
 * seq 오름차순 정렬.
 */
export const formatConflictSeqLabels = (
  conflicts: DuplicateRecipientConflict[],
): string => {
  if (conflicts.length === 0) return '';
  return [...conflicts]
    .sort((a, b) => a.seq - b.seq)
    .map((c) => `#${c.seq}`)
    .join(', ');
};
